import React, {ReactNode, useCallback} from "react";
import {HeadlineContentBlockView} from "./HeadlineContentBlockView";
import {RichTextContentBlockView} from "./RichTextContentBlockView";
import {WebEmbedContentBlockView} from "./WebEmbedContentBlockView";
import {MultiMediaContentBlockView, MultiMediaContentBlockViewExportedProps} from "./MultiMediaContentBlockView";
import {QuoteContentBlockView} from "./QuoteContentBlockView";
import {IAbstractContentBlock, IEvent, IGame} from "@webng-types/write-model";
import {ScoreboardContentBlockView} from "./ScoreboardContentBlockView";
import {SportEventContentBlockView, SportEventContentBlockViewContainer} from "./SportEventContentBlockView";
import {PlayerCardContentBlockView} from "./PlayerCardContentBlockView";
import {EmojiContentBlockView} from "./EmojiContentBlockView";
import {QuizContentBlockView} from "./QuizContentBlockView";
import {
  LiveblogEvent,
  useExternalUserProvider,
  useTrackingFunction,
  useUseFunctionalCookies
} from "../LiveblogRenderContext";
import {SportEventContentView} from "./SportEventContentView";
import {EventExtensionRenderer} from "../extensions/EventExtensionRenderer";
import {ChannelPreview} from "../chunks/Comments";
import {ViafouraPreview} from "../viafoura/ViafouraPreview";
import {EventExtension} from "../extensions/Extension";
import classnames from "classnames";
import {PollContentBlockView} from "./PollContentBlockView";
import {EventReference} from "./EventReference";
import {ChevronDown} from "react-feather";
import {useTranslation} from "react-i18next";
import FoldInButton from "./components/FoldInButton";

interface ContentBlockViewProps extends MultiMediaContentBlockViewExportedProps {
  game: IGame
  event: IEvent
  block: IAbstractContentBlock
  position?: number
  isPreview?: boolean
}

// function unknownKind(block: never): never {
//   throw new Error("Unknown Content Block type " + block);
// }


function ContentBlockSwitcherView({block, position, isPreview, event, ...props} : ContentBlockViewProps) {
  switch(block._type) {
    case "Tik::Model::Content::HeadlineContentBlock":
      return <ContentBlockWrapper event={event} classPostfix="headline" position={position}>
        <HeadlineContentBlockView block={block} {...props} />
      </ContentBlockWrapper>
    case "Tik::Model::Content::RichTextContentBlock":
      return <ContentBlockWrapper event={event} classPostfix="rich-text" position={position}>
        <RichTextContentBlockView block={block} {...props} />
      </ContentBlockWrapper>
    case "Tik::Model::Content::WebEmbedContentBlock":
      return <ContentBlockWrapper event={event} classPostfix="web-embed" position={position}>
        <WebEmbedContentBlockView eventId={event.local_id} block={block} isPreview={isPreview} {...props} />
      </ContentBlockWrapper>
    case "Tik::Model::Content::MultiMediaContentBlock":
      return <ContentBlockWrapper event={event} classPostfix="multi-media" position={position}>
        <MultiMediaContentBlockView event={event} block={block} {...props} />
      </ContentBlockWrapper>
    case "Tik::Model::Content::QuoteContentBlock":
      return <ContentBlockWrapper event={event} classPostfix="quote" position={position}>
        <QuoteContentBlockView block={block} {...props} />
      </ContentBlockWrapper>
    case "Tik::Model::Content::SportEventContentBlock":
      return <ContentBlockWrapper event={event} classPostfix="sport-event" classPostfixAddition={block.event_type?.replace('.', '-')} position={position}>
        <SportEventContentBlockView block={block} {...props} />
      </ContentBlockWrapper>
    case "Tik::Model::Content::ScoreboardContentBlock":
      return <ContentBlockWrapper event={event} classPostfix="scoreboard" position={position}>
        <ScoreboardContentBlockView block={block} {...props} />
      </ContentBlockWrapper>
    case "Tik::Model::Content::AbstractContentBlock":
      return <ContentBlockWrapper event={event} classPostfix="none" position={position}>
        <React.Fragment></React.Fragment>
      </ContentBlockWrapper>
    case "Tik::Model::Content::PlayerCardContentBlock":
      return <ContentBlockWrapper event={event} classPostfix="none" position={position}>
        <PlayerCardContentBlockView block={block} {...props} />
      </ContentBlockWrapper>
    case "Tik::Model::Content::EmojiContentBlock":
      return <ContentBlockWrapper event={event} classPostfix="emoji" position={position}>
        <EmojiContentBlockView block={block} {...props} />
      </ContentBlockWrapper>
    case "Tik::Model::Content::PollContentBlock":
      return <ContentBlockWrapper event={event} classPostfix="poll" position={position}>
        <PollContentBlockView event={event} block={block} isPreview={isPreview} {...props} />
      </ContentBlockWrapper>
    case "Tik::Model::Content::QuizContentBlock":
      return <ContentBlockWrapper event={event} classPostfix="quiz" position={position}>
        <QuizContentBlockView event={event} block={block} {...props} />
      </ContentBlockWrapper>
    default:
      return <React.Fragment></React.Fragment>;
      // return unknownKind(block)
  }
}

function ContentBlockWrapper({children, classPostfix, classPostfixAddition, position, event} : {children: ReactNode, classPostfix: string, classPostfixAddition?: string, position?: number, event: IEvent}) {
  const classNames = classnames('tik4-content-block',
    {
      [`tik4-content-block--${classPostfix}`]: classPostfix,
      [`tik4-content-block--${classPostfix}--${classPostfixAddition}`]: classPostfixAddition,
      [`tik4-content-block--position-${position}`]: position,
      [`tik4-content-block--hlimi`]: (position && event.fold_out_index && (position > event.fold_out_index)),

    }
  )
  return <div className={classNames}>
    {children}
  </div>
}

export interface ContentBlocksViewProps extends MultiMediaContentBlockViewExportedProps {
  game: IGame
  event: IEvent
  comments?: {
    version: number
    ownerId: string
    channelId: string
    displayMode?: 'modal' | 'inline'
  }
  viafouraConversation?: {
    containerId: string
  }
  eventExtension?: EventExtension
  isPreview?: boolean
  toggleBreakpoint?: () => void
  isBreakpointActive?: boolean
}

export function ContentBlocksView({game,event, comments, viafouraConversation, eventExtension, isPreview, toggleBreakpoint, isBreakpointActive, ...props}: ContentBlocksViewProps) {
  const externalUserProvider = useExternalUserProvider()
  const useFunctionalCookies =  useUseFunctionalCookies()
  const trackingFunction = useTrackingFunction()
  const {t} = useTranslation("liveblog")
  // const navigate = useNavigate();
  // const localGame = useLocalGame();

  // const handleCommentManage = useCallback(function handleCommentManage(commentId: string){
  //   navigate(commentRoute(commentId, localGame.local_id!));
  // }, [navigate, localGame.local_id])
  let i = 0

  const prefilledTrackingFunction = useCallback( (e: LiveblogEvent) => {
    trackingFunction({...e, e: event.local_id})
  }, [trackingFunction, event.local_id])

  return <>
    { event.sport_event && event.sport_event.event_type &&
      <div className={`tik4-content-block tik4-content-block--sport-event tik4-content-block--position-${++i}`}><SportEventContentBlockViewContainer><SportEventContentView block={event.sport_event} game={game} /></SportEventContentBlockViewContainer></div>
    }
    <EventExtensionRenderer event={event} Extension={eventExtension?.AboveContent} />
    {event.contents?.map((block) => {
      // filter empty rich text blocks
      if(block && block._type === "Tik::Model::Content::RichTextContentBlock" && !block.text) {
        return <React.Fragment key={block.local_id}></React.Fragment>;
      }
      return <ContentBlockSwitcherView game={game} event={event} block={block} key={block.local_id} position={++i}
                                isPreview={isPreview} {...props} />
    })}
    {/* breakpoint hlim */}
    { !!event.fold_out_index && isBreakpointActive &&
      <div className="tik4-hlimi">
        <button className="tik4-hlimi__link tik4-link" onClick={toggleBreakpoint}>
          <ChevronDown className="tik4-icon tik4-icon--mr"/>
          <span className="tik4-hlimi__link__text">
              {t('liveblog.event.fold_out.button')}
            </span>
        </button>
      </div>
    }

    {event.reference && event.reference._type === "Tik::Model::Event" &&
      <div className={`tik4-content-block tik4-content-block--event-reference tik4-content-block--position-${++i}`}>
        <EventReference game={game} reference={event.reference} isPreview={isPreview}/>
      </div>
    }
    <EventExtensionRenderer event={event} Extension={eventExtension?.BelowContent} />
    {comments && <ChannelPreview ownerId={comments.ownerId} channelId={comments.channelId} externalUserProvider={externalUserProvider} version={comments.version} position={++i} useCookies={useFunctionalCookies} trackingFunction={prefilledTrackingFunction} displayMode={comments.displayMode} />}
    {/* Gated on isPreview, unlike the native ChannelPreview next to it. Viafoura resolves the
        account from the page's hostname, and the editor runs on a Tickaroo domain that is
        deliberately not provisioned with Viafoura — so a conversation opened there could never be
        the customer's. The card shows, the action does not (design D22). */}
    {viafouraConversation && <ViafouraPreview containerId={viafouraConversation.containerId} position={++i} trackingFunction={prefilledTrackingFunction} disabled={isPreview} />}
    <EventExtensionRenderer event={event} Extension={eventExtension?.BelowComments} />

    { !!event.fold_out_index && !isBreakpointActive && toggleBreakpoint &&
      <FoldInButton onClick={toggleBreakpoint} className="tik4-fold-in--hlimi" />
    }
  </>
}
